import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImageIcon, Search, Upload, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

import { useMediaListQuery } from '@/client/api/cms/media.query';
import { useUploadMediaMutation } from '@/client/api/cms/media.mutation';
import { CMSAsset } from '@/client/types/cms';

interface MediaPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: CMSAsset) => void;
  /** Restrict picker to image types (default: true). */
  imagesOnly?: boolean;
}

const MediaPickerDialog = ({
  open,
  onClose,
  onSelect,
  imagesOnly = true,
}: MediaPickerDialogProps) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useMediaListQuery(page, {
    q: search || undefined,
    mime: imagesOnly ? 'image' : 'all',
  });
  const uploadMutation = useUploadMediaMutation();

  const assets: CMSAsset[] = data?.assets ?? [];

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const result = await uploadMutation.mutateAsync(files[0]);
      toast.success('Uploaded');
      if (result?.data) onSelect(result.data);
    } catch (err: any) {
      toast.error(err?.message ?? 'Upload failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-blue-600" />
            Insert media
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 pb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search by filename or alt text…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={imagesOnly ? 'image/*' : 'image/*,video/*'}
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
          <Button onClick={() => inputRef.current?.click()} disabled={uploadMutation.isPending} className="gap-2">
            <Upload className="h-4 w-4" />
            {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square w-full rounded-md" />
              ))}
            </div>
          )}

          {!isLoading && assets.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ImageIcon className="h-10 w-10 text-gray-300 mb-3" />
              <p className="text-sm text-gray-600">No media yet</p>
              <p className="text-xs text-gray-400">Upload an image to get started.</p>
            </div>
          )}

          {!isLoading && assets.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {assets.map(asset => (
                <button
                  key={asset.asid}
                  type="button"
                  onClick={() => onSelect(asset)}
                  className="group relative aspect-square overflow-hidden rounded-md border border-gray-200 bg-gray-50 hover:border-blue-400 hover:shadow-md transition-all"
                >
                  {asset.mime_type?.startsWith('image/') ? (
                    <img
                      src={asset.public_url}
                      alt={asset.alt_text ?? asset.filename ?? ''}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-gray-500">
                      {asset.mime_type}
                    </div>
                  )}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">
                    {asset.filename ?? asset.asid}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between pt-3 border-t">
            <span className="text-xs text-gray-500">
              Page {data.page} of {data.total_pages} · {data.total_assets} item(s)
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose} className="gap-1">
            <X className="h-4 w-4" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaPickerDialog;
